import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit'
import API from 'utils/api'
import { buildUrl, clamp } from 'utils/helpers'
import config from 'config'
import { showErrorMessage, showInfoMessage } from 'store/ui/actions'
import { updateServiceNote } from 'features/DeviceServiceNote/model/updateServiceNote'

const REPLACED = 'REPLACED'
const resolveLatest = (fn) => {
  const current = { value: {} }
  return (...args) => {
    const now = {}
    current.value = now
    return Promise.resolve(args)
      .then((args) => fn(...args))
      .then((resolve) =>
        current.value === now ? resolve : Promise.reject(REPLACED)
      )
  }
}

export const updateTmz = createAsyncThunk(
  'devices/update_tmz',
  async ({ deviceUuid, tmz }, { getState, rejectWithValue, dispatch }) => {
    try {
      const payload = await API.request(`/devices/${deviceUuid}/update_tmz`, {
        method: 'PUT',
        body: JSON.stringify({ tmz }),
      })
      dispatch(showInfoMessage(payload?.message))
      return Promise.resolve(payload)
    } catch (err) {
      const { error } = await Promise.resolve(err.json())
      dispatch(showErrorMessage(error))
      return rejectWithValue(error)
    }
  }
)

export const UpdateDeviceServices = createAsyncThunk(
  'devices/update_services',
  async (
    { services, device_uuid, device_type },
    { getState, rejectWithValue, dispatch }
  ) => {
    try {
      const payload = await API.request(`/services/update_batch`, {
        method: 'PUT',
        body: JSON.stringify({ services, device_uuid, device_type }),
      })
      dispatch(showInfoMessage(payload?.message))
      return Promise.resolve(payload)
    } catch (err) {
      const { error } = await Promise.resolve(err.json())
      dispatch(showErrorMessage(error))
      return rejectWithValue(error)
    }
  }
)

export const exportDevicesReport = createAsyncThunk(
  'devices/export',
  async ({ email }, { rejectWithValue, dispatch }) => {
    try {
      const payload = await API.request(`/reports`, {
        method: 'POST',
        body: JSON.stringify({ email }),
      })
      dispatch(showInfoMessage(payload?.message))
      return Promise.resolve(payload)
    } catch (err) {
      const { error } = await Promise.resolve(err.json())
      dispatch(showErrorMessage(error))
      return rejectWithValue(error)
    }
  }
)

export const fetchDeviceServiceCatalog = createAsyncThunk(
  'devices/services_catalog',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const payload = await API.request(buildUrl(`/services/catalog`, params), {
        method: 'GET',
      })
      return payload
    } catch (err) {
      const { error } = await Promise.resolve(err.json())
      dispatch(showErrorMessage(error))
      return rejectWithValue(error)
    }
  }
)

export const updateOsd = createAsyncThunk(
  'devices/update_tmz',
  async (deviceUuid, { getState, rejectWithValue, dispatch }) => {
    try {
      const payload = await API.request(`/devices/${deviceUuid}/update_osd`, {
        method: 'PUT',
      })
      dispatch(showInfoMessage(payload?.message))
      return Promise.resolve(payload)
    } catch (err) {
      const { error } = await Promise.resolve(err.json())
      dispatch(showErrorMessage(error))
      return rejectWithValue(error)
    }
  }
)

export const updateCameraNotice = createAsyncThunk(
  'devices/updateCameraNotice',
  async ({ deviceUuid, notice }, { rejectWithValue }) => {
    try {
      const payload = await API.request(`/cameras/${deviceUuid}/notice`, {
        method: 'PATCH',
        body: JSON.stringify({ notice }),
      })
      return { deviceUuid, notice: payload.notice }
    } catch (err) {
      const payload = await err.json()
      return rejectWithValue(payload.error || payload.message)
    }
  }
)

export const fetchDevices = createAsyncThunk(
  'devices/fetchList',
  async (
    {
      search,
      limit = 20,
      page = 1,
      filters = {
        is_installed: undefined,
        is_online: undefined,
      }, //  { linked: null, is_online: null }
      sort = { by: 'address', order: 'desc' }, //  { by: null, order: null }
      address_uuid = undefined,
      device_type = undefined,
      // Новые параметры контракта business_apps#1486 §6.2 (model_family[],
      // firmware_status[], dynamic_keys_state[], installed_since …) — как есть.
      params = {},
      // Ещё один аргумент — latestOnly, его читает редьюсер (см. ниже).
    },
    { getState, rejectWithValue, dispatch }
  ) => {
    try {
      const call = resolveLatest(() =>
        API.request(
          `${buildUrl(`/devices`, {
            search,
            limit,
            page,
            device_type,
            address_uuid,
            sort_by: sort?.by,
            order: sort?.order,
            is_online: filters?.is_online,
            is_installed: filters?.is_installed,
            ...params,
          })}`,
          {},
          `${config.redirectUri}/api/v2`
        )
      )
      // { devices, meta }
      const { devices, meta } = await call()

      return { devices, meta, isFirstPage: page === 1 }
    } catch (err) {
      console.log('err', err)
      // Сетевая ошибка и HTML от балансировщика приходят без JSON — не падаем
      // на разборе; payload, как и раньше, — только message ответа.
      const body =
        typeof err?.json === 'function'
          ? await err.json().catch(() => ({}))
          : {}
      return rejectWithValue(body?.message)
    }
  }
)

/*
export const getIntercomsByIdentifier = (
  identifier: string,
  limit: string = 20,
  page: number = 1,
  filters: {
    linked: null,
    is_online: null,
  },
  sort: {
    by: null,
    order: null,
  },
) => async (dispatch) => {
  dispatch(actions.getIntercomsByIdentifierRequest(identifier, limit, page))

  try {
    const setFilters = () => {
      let params = []
      if (filters.is_online !== null && filters.is_online !== undefined)
        params.push(`is_online=${filters.is_online}`)
      if (filters.linked !== null && filters.linked !== undefined)
        params.push(`linked=${filters.linked === true ? filters.linked : ''}`)
      params = params.join('&')
      return params.length > 0 ? `&${params}` : params
    }

    const intercoms = await Api.request(
      `/intercoms?q=${identifier}&page=${page}&limit=${limit}&sort_by=${
        sort?.by
      }&order=${sort?.order}${setFilters()}`,
    )
    dispatch(actions.getIntercomsByIdentifierSuccess(intercoms, page === 1))
    return Promise.resolve(intercoms)
  } catch (err) {
    console.log(err)
    const { message } = await err.json()
    dispatch(actions.getIntercomsByIdentifierError(message))
    return Promise.reject(err)
  }
}
*/

// Одно устройство лежит сразу в list и в intercoms/cameras (страница дома читает
// их), поэтому правку применяем ко всем трём. Списка может не быть: его обнуляет
// clearDevices, а ответ сохранения приходит позже.
const updateDeviceFields = (state, deviceUuid, fields) => {
  const lists = [state.list, state.intercoms, state.cameras]

  lists.forEach((devices) => {
    const device = devices?.find(({ uuid }) => uuid === deviceUuid)
    if (device) Object.assign(device, fields)
  })
}

const initialState = {
  current: null,
  list: null,
  totalPages: null,
  total: null,
  // meta.counters { online, offline, unknown, outdated, uninstalled } по
  // текущим фильтрам (новая главная, business_apps#1486 §5.7); null, пока
  // бэкенд их не отдаёт.
  counters: null,
  latestRequestId: null,
  page: 1,
  limit: 25,
  search: '',
  available: [],
  loadingAvailable: false,
  pending: false,
  intercoms: null,
  cameras: null,
}

// reducers
export const devicesSlice = createSlice({
  name: 'devices',
  initialState,
  reducers: {
    clearDevices: () => initialState,
    incPage: (state) => {
      state.page = clamp(state.page + 1, 1, state.pages)
    },
    decPage: (state) => {
      state.page = clamp(state.page - 1, 1, state.pages)
    },
    editDevice: (state, { payload: { deviceUuid, fields } }) => {
      updateDeviceFields(state, deviceUuid, fields)
    },
    setSearch: (state, { payload }) => {
      state.page = 1
      state.search = payload
    },
  },
  extraReducers: {
    // latestOnly (новая главная, business_apps#1486): в стор попадает только
    // ответ последнего такого запроса — поиск, фильтры и докрутка не
    // перемешивают выдачи, а ответ после ухода со страницы (clearDevices
    // сбрасывает latestRequestId) не перетирает чужой список. Остальные
    // вызовы ведут себя как раньше.
    [fetchDevices.pending]: (state, { meta }) => {
      state.pending = true
      if (meta?.arg?.latestOnly) state.latestRequestId = meta.requestId
    },
    [fetchDevices.fulfilled]: (
      state,
      { payload: { isFirstPage, meta, devices }, meta: action }
    ) => {
      // пока список новой главной активен (latestRequestId задан), поздние
      // ответы других страниц (дом, подъезд, гарантия) его не перетирают
      const stale = action?.arg?.latestOnly
        ? action.requestId !== state.latestRequestId
        : Boolean(state.latestRequestId)
      if (stale) return
      state.pending = false
      state.totalPages = meta.total_pages
      state.total = meta.total
      state.counters =
        meta.counters && typeof meta.counters === 'object'
          ? meta.counters
          : null
      state.list = isFirstPage ? devices : [...state.list, ...devices]

      const intercoms = devices.filter((d) => d.type === 'intercom')
      const cameras = devices.filter((d) => d.type === 'camera')

      state.intercoms = isFirstPage
        ? intercoms
        : [...(state?.intercoms || []), ...intercoms]
      state.cameras = isFirstPage
        ? cameras
        : [...(state?.cameras || []), ...cameras]
    },
    [fetchDevices.rejected]: (state, { error, payload, meta }) => {
      const stale = meta?.arg?.latestOnly
        ? meta.requestId !== state.latestRequestId
        : Boolean(state.latestRequestId)
      if (stale) return
      state.pending = false
    },
    [updateTmz.pending]: (state) => {
      state.updatingTimezone = true
    },
    [updateTmz.fulfilled]: (state, { payload }) => {
      state.updatingTimezone = false
      state.error = payload?.message
    },
    [updateTmz.rejected]: (state, { payload }) => {
      state.updatingTimezone = false
      state.error = payload?.error
    },
    [updateOsd.pending]: (state) => {
      state.updatingOsd = true
    },
    [updateOsd.fulfilled]: (state, { payload }) => {
      state.updatingOsd = false
      state.error = payload?.message
    },
    [updateOsd.rejected]: (state, { payload }) => {
      state.updatingOsd = false
      state.error = payload?.error
    },
    [updateCameraNotice.fulfilled]: (
      state,
      { payload: { deviceUuid, notice } }
    ) => {
      updateDeviceFields(state, deviceUuid, { notice })
    },
    [updateServiceNote.fulfilled]: (
      state,
      { payload: { deviceUuid, serviceNote } }
    ) => {
      updateDeviceFields(state, deviceUuid, { alias: serviceNote })
    },
    [fetchDeviceServiceCatalog.pending]: (state) => {
      state.fetchingServiceCatalog = true
    },
    [fetchDeviceServiceCatalog.fulfilled]: (state, { payload }) => {
      state.deviceServiceCatalog = payload
      state.fetchingServiceCatalog = false
    },
    [fetchDeviceServiceCatalog.rejected]: (state, { payload }) => {
      state.fetchingServiceCatalog = false
      state.error = payload?.error
    },
    [UpdateDeviceServices.pending]: (state) => {
      state.UpdateDeviceServices = true
    },
    [UpdateDeviceServices.fulfilled]: (state, { payload }) => {
      state.deviceServiceCatalog = payload
      state.UpdateDeviceServices = false
    },
    [UpdateDeviceServices.rejected]: (state, { payload }) => {
      state.UpdateDeviceServices = false
      state.error = payload?.error
    },
  },
})

export const { setSearch, clearDevices, editDevice } = devicesSlice.actions

export default devicesSlice.reducer
